import { ChevronRightIcon, Link2, Link2Off, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { Item, ItemContent, ItemDescription, ItemTitle } from '@/components/ui/item'

export type RiskReductionMeasureLinkedSafetyFunction = {
  id: string
  name: string
  eplanReferenceId: string | null
  description: string | null
}

type RiskReductionMeasureLinkedSafetyFunctionsSectionProps = {
  linkedSafetyFunction: RiskReductionMeasureLinkedSafetyFunction | null
  isUnlinking: boolean
  onUnlinkClick: () => void
  onAssignClick: () => void
  isAssignDisabled: boolean
}

export function RiskReductionMeasureLinkedSafetyFunctionsSection({
  linkedSafetyFunction,
  isUnlinking,
  onUnlinkClick,
  onAssignClick,
  isAssignDisabled,
}: RiskReductionMeasureLinkedSafetyFunctionsSectionProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const hasLinkedSafetyFunction = linkedSafetyFunction != null

  return (
    <section className='space-y-3'>
      <h2 className='text-lg font-semibold tracking-tight'>
        {t('riskReductionMeasuresLibraryPage.rrmDetail.linkedSafetyFunctionsTitle')}
      </h2>
      {!hasLinkedSafetyFunction && (
        <Empty className='rounded-lg border border-dashed py-8'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <Link2 className='size-6' />
            </EmptyMedia>
            <EmptyTitle>
              {t(
                'riskReductionMeasuresLibraryPage.rrmDetail.linkedSafetyFunctionsEmptyTitle'
              )}
            </EmptyTitle>
            <EmptyDescription>
              {t(
                'riskReductionMeasuresLibraryPage.rrmDetail.linkedSafetyFunctionsEmptyDescription'
              )}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              size='sm'
              onClick={onAssignClick}
              disabled={isAssignDisabled}
            >
              {t(
                'riskReductionMeasuresLibraryPage.actions.assignSafetyFunction'
              )}
            </Button>
          </EmptyContent>
        </Empty>
      )}
      {hasLinkedSafetyFunction && (
        <ul className='space-y-1'>
          <li>
            <Item variant='outline' size='sm'>
              <ItemContent className='min-w-0 flex-1'>
                <ItemTitle className='cursor-default text-foreground'>
                  {linkedSafetyFunction.name}
                </ItemTitle>
                {linkedSafetyFunction.eplanReferenceId != null ? (
                  <ItemDescription className='text-xs text-muted-foreground'>
                    {t('safetyFunctionsPage.card.eplanReferenceId', {
                      referenceId: linkedSafetyFunction.eplanReferenceId,
                    })}
                  </ItemDescription>
                ) : null}
                {linkedSafetyFunction.description != null &&
                linkedSafetyFunction.description.trim().length > 0 ? (
                  <ItemDescription className='line-clamp-2 text-xs text-muted-foreground'>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <span className='block cursor-default'>
                          {linkedSafetyFunction.description}
                        </span>
                      </HoverCardTrigger>
                      <HoverCardContent
                        side='top'
                        className='max-w-sm text-sm text-muted-foreground'
                      >
                        <p className='whitespace-pre-wrap'>
                          {linkedSafetyFunction.description}
                        </p>
                      </HoverCardContent>
                    </HoverCard>
                  </ItemDescription>
                ) : null}
              </ItemContent>
              <div className='flex shrink-0 items-center gap-1'>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0 text-muted-foreground hover:text-destructive'
                  onClick={onUnlinkClick}
                  disabled={isUnlinking}
                  aria-label={t(
                    'riskReductionMeasuresLibraryPage.rrmDetail.unlinkFromSafetyFunctionAria',
                    { safetyFunction: linkedSafetyFunction.name }
                  )}
                >
                  {isUnlinking ? (
                    <Loader2 className='size-4 animate-spin' />
                  ) : (
                    <Link2Off className='size-4' />
                  )}
                </Button>
                <Link
                  relative='route'
                  to={`../safety-functions/${linkedSafetyFunction.id}`}
                  className='inline-flex size-8 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground'
                  aria-label={linkedSafetyFunction.name}
                >
                  <ChevronRightIcon className='size-4' />
                </Link>
              </div>
            </Item>
          </li>
        </ul>
      )}
    </section>
  )
}
